import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import * as Sentry from "@sentry/react";
import { AuthProvider } from "./contexts/AuthContext";
import { msalConfig } from "./config/authConfig";
import App from "./App.tsx";
import "./index.css";

// Initialize Sentry for error tracking and performance monitoring
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE || "1.0.0",
  
  integrations: [
    // Browser tracing for performance monitoring
    Sentry.browserTracingIntegration(),
    // Capture React component errors with session replay
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  
  // Performance monitoring sample rate (adjust for production)
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  
  // Session replay sample rate
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  
  // Enable stack traces for errors
  attachStacktrace: true,
  
  // Capture unhandled promise rejections
  beforeSend(event, hint) {
    // You can modify or filter events here
    const error = hint.originalException;
    if (error && typeof error === 'object' && 'message' in error) {
      // Add custom context for network errors
      if ((error as Error).message?.includes('Network Error') || 
          (error as Error).message?.includes('ERR_CONNECTION')) {
        event.tags = {
          ...event.tags,
          error_type: 'network_error',
        };
      }
    }
    return event;
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const msalInstance = new PublicClientApplication(msalConfig);
await msalInstance.initialize();

// Handle redirect promise before rendering
// This ensures MSAL processes the auth callback before React Router
await msalInstance.handleRedirectPromise().catch((error) => {
  console.error('MSAL redirect error:', error);
  Sentry.captureException(error, {
    tags: { component: 'msal', action: 'redirect' },
  });
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
          <div className="text-center p-8 bg-white rounded-lg shadow-lg">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-4">We're sorry, but an unexpected error occurred.</p>
            <p className="text-sm text-gray-500 mb-4">{error?.toString()}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      )}
      onError={(error) => {
        Sentry.captureException(error);
      }}
    >
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <App />
          </AuthProvider>
        </QueryClientProvider>
      </MsalProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
